import React from "react";
import {
  Flex, SimpleGrid, Stack, Text, Link,
  StatCard, Card, DataTable, EmptyState, Tag, InlineAlert, Skeleton,
} from "@pinpoint/design-system";

/** Fires `onActivate` on click or on Enter/Space, for elements acting as a button without an `href`. */
const activateOnKey = (onActivate) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onActivate();
  }
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) : "");
const fmtDuration = (a, b) => {
  if (!a || !b) return "—";
  const m = Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

export function StatRow({ stats, certCount }) {
  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
      <StatCard accent label="Visits" value={stats.visits}
        delta={stats.last30 ? { value: `+${stats.last30}`, direction: "up" } : undefined} hint="last 30 days" />
      <StatCard label="Hours logged" value={stats.hours} hint="all time" />
      <StatCard label="Distinct spaces" value={stats.labsVisited} hint="visited all time" />
      {/* Tools the user may use: ungated tools plus those their credentials unlock. */}
      <StatCard label="Tools available" value={certCount} hint="ready to use" />
    </SimpleGrid>
  );
}

export function ActionLink({ label, onActivate }) {
  return (
    <Link
      role="button" tabIndex={0}
      fontSize="xs" fontWeight={600} color="semantic.accent" cursor="pointer"
      onClick={(e) => { e.preventDefault(); onActivate?.(); }}
      onKeyDown={activateOnKey(() => onActivate?.())}
    >
      {label}
    </Link>
  );
}

export function RecentVisits({ visits, spaceNames }) {
  const columns = [
    { key: "date", header: "Date" },
    { key: "lab", header: "Space" },
    { key: "duration", header: "Duration", align: "right" },
    { key: "status", header: "Status" },
  ];
  const rows = visits.map((v) => ({
    date: <Text as="span" textStyle="code">{fmtDate(v.startedAt)}</Text>,
    lab: spaceNames[v.space_id]
      ? spaceNames[v.space_id]
      : <Text as="span" textStyle="code">{v.space_id}</Text>,
    duration: <Text as="span" textStyle="code">{fmtDuration(v.startedAt, v.endedAt)}</Text>,
    status:
      v.status === "active" ? <Tag label="Active" tone="success" />
      : v.autoEnded || v.status === "auto_ended" ? <Tag label="Auto sign-out" tone="warning" />
      : <Tag label="Ended" tone="neutral" />,
  }));
  return (
    <DataTable
      columns={columns}
      rows={rows}
      emptyState={<EmptyState title="No visits yet" description="Your visits will show up here once you sign in at a makerspace." />}
    />
  );
}

export function ManagedSpaces({ spaces, spaceNames }) {
  return (
    <Card>
      <Stack gap={2}>
        {spaces.map((p) => (
          <Flex key={p.lab} justify="space-between" align="center">
            <Text fontSize="sm" color="gray.800">{spaceNames[p.lab] || p.lab}</Text>
            <Tag label="Admin" tone="brand" />
          </Flex>
        ))}
      </Stack>
    </Card>
  );
}

/*
 * Tools the user may use. Tools without a credential requirement are open to
 * everyone, so they are listed even before a credential account is linked;
 * linking only adds tools that need a credential.
 */
export function Certifications({ tools, connected, onConnect }) {
  const list = (
    <DataTable
      columns={[{ key: "name", header: "Tool" }]}
      rows={tools.map((t) => ({ name: t.name }))}
      emptyState={<EmptyState title="No tools available yet" description="Tools you can use will appear here." />}
    />
  );
  if (connected) return list;
  const prompt = (
    <InlineAlert
      status="warning"
      title="Connect your credential account"
      description="Link your credential provider in Settings to unlock tools that require a credential."
      action={<ActionLink label="Connect →" onActivate={onConnect} />}
    />
  );
  if (tools.length === 0) return prompt;
  return <Stack gap={3}>{prompt}{list}</Stack>;
}

export function DashboardSkeletons() {
  return (
    <Stack gap={6}>
      <Skeleton variant="rect" h="64px" />
      <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rect" h="92px" />)}
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
        {[0, 1, 2].map((i) => <Skeleton key={i} variant="rect" h="120px" />)}
      </SimpleGrid>
    </Stack>
  );
}

export { fmtTime };
